import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import bgSpain from '../assets/bg_spain.jpg';
import cidadania4uLogo from '../assets/cidadania4u-logohorizontal-white.png';
import cidIcon from '../assets/cid.png';
import { useSSO } from '../hooks/useSSO';

export const LoginPage: React.FC = () => {
  const { login, isLoading, isAuthenticated } = useAuth();
  const { loginWithMicrosoft, isSSOAvailable, isLoading: ssoLoading } = useSSO();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/inicio');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      const result = await login(formData);
      console.log('Resultado do login:', result);
      
      if (result.success) {
        navigate('/');
      } else {
        // Tratar erros específicos
        let errorMessage = 'Erro desconhecido';
        
        if (typeof result.error === 'string') {
          errorMessage = result.error;
        } else if (result.error && typeof result.error === 'object') {
          errorMessage = (result.error as any).message || (result.error as any).toString() || 'Erro desconhecido';
        }
        
        if (errorMessage.includes('Invalid login credentials')) {
          setError('Email ou senha incorretos. Verifique suas credenciais e tente novamente.');
        } else if (errorMessage.includes('Email not confirmed')) {
          setError('Email não confirmado. Verifique sua caixa de entrada e confirme sua conta.');
        } else if (errorMessage.includes('Too many requests')) {
          setError('Muitas tentativas de login. Aguarde alguns minutos e tente novamente.');
        } else if (errorMessage.includes('User not found')) {
          setError('Usuário não encontrado. Verifique se o email está correto.');
        } else {
          setError('Erro de autenticação. Tente novamente em alguns instantes.');
        }
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setError('Erro de conexão. Verifique sua internet e tente novamente.');
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');

    const result = await loginWithMicrosoft();
    if (result.success) {
      // Redirecionar para a página inicial após login bem-sucedido
      window.location.href = '/';
    } else {
      setError(result.error || 'Erro no login Microsoft');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-background">
          <img 
            src={bgSpain} 
            alt="Background Espanha" 
            className="login-background-image"
          />
          <div className="login-background-overlay"></div>
          <div className="login-brand">
            <img src={cidadania4uLogo} alt="Cidadania4u" className="login-brand-logo" />
          </div>
          <div className="login-info-panel">
            <h1 className="login-info-title">Checklist Espanha</h1>
            <p className="login-info-subtitle">
              Sistema de gerenciamento de documentos para cidadania espanhola
            </p>
          </div>
        </div>
      </div>
      
      <div className="login-right">
        <div className="login-form-container">
          <div className="login-robot">
            <div className="robot-icon">
              <img src={cidIcon} alt="Cidadania4u" className="robot-icon-img" />
            </div>
            <h2 className="login-greeting">Olá, Cidadanier!</h2>
          </div>
          
          <div className="login-form">
            {/* Formulário de Login Tradicional */}
            <form onSubmit={handleSubmit} className="login-form-fields">
              <div className="login-input-group">
                <div className="login-input-wrapper">
                  <FontAwesomeIcon icon={faUser} className="login-input-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="login-input"
                    required
                  />
                </div>
              </div>

              <div className="login-input-group">
                <div className="login-input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="login-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Senha"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="login-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="login-password-toggle"
                    title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>

            {error && (
              <div className="login-error" role="alert" aria-live="polite">
                <div className="login-error-icon">⚠️</div>
                <div className="login-error-message">{error}</div>
              </div>
            )}

            {/* Divisor */}
            <div className="login-divider">
              <span>ou</span>
            </div>

            {/* Botão de Login Microsoft */}
            {isSSOAvailable ? (
              <button
                type="button"
                onClick={handleMicrosoftLogin}
                className="login-microsoft-button"
                disabled={ssoLoading || isLoading}
              >
                <FontAwesomeIcon icon={faMicrosoft} className="microsoft-icon" />
                {ssoLoading ? 'Conectando...' : 'Entrar com Microsoft'}
              </button>
            ) : (
              <div className="login-error">
                SSO Microsoft não disponível. Verifique as configurações.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
